/* eslint-disable no-nested-ternary */
import React, { useCallback, useMemo } from 'react'

import MapLink from 'client/components/Map/Link'
import Stars from 'client/components/Stars'

import { MEASUREMENT_UNITS } from '@s4l/stayforlong/shared/MeasurementSystem'
import { ONE_KILOMETER_IN_MILES } from 'src/constants/convertUnits'
import { useMarket } from 'src/hooks/useMarket'

import styles from './styles.module.css'
import Location from 'client/icons/Location'
import { useS4LContext } from '@s4l/react-context'

interface PropertyTitleProps {
  address: string
  distance: number
  isInCenter: boolean
  name: string
  propertyId: number
  stars: number
}

const PropertyTitle: React.FC<PropertyTitleProps> = ({
  address,
  distance,
  isInCenter,
  name,
  propertyId,
  stars,
}) => {
  const { i18n } = useS4LContext()
  const { measurementSystem } = useMarket()

  const marketMeasurementSystem = measurementSystem || MEASUREMENT_UNITS.METRIC
  const distanceInMarketUnit = useMemo(
    () =>
      marketMeasurementSystem === MEASUREMENT_UNITS.IMPERIAL
        ? distance * ONE_KILOMETER_IN_MILES
        : distance,
    [marketMeasurementSystem, distance]
  )

  const distanceFromCenter = useMemo(
    () => ({ distance: distance ? Math.round(distanceInMarketUnit * 10) / 10 : 0 }),
    [distance, distanceInMarketUnit]
  )

  const locationMessage = useCallback(() => {
    if (isInCenter) {
      return ` · ${i18n.formatMessage({ id: 'accommodation.in-center' })}`
    }

    if (distance) {
      return ` · ${i18n.formatMessage({ id: 'accommodation.center-distance' }, distanceFromCenter)}`
    }

    return null
  }, [isInCenter, distance, distanceFromCenter, i18n])

  return (
    <div className={styles.propertyTitleBase}>
      <header className={styles.header}>
        <h1 data-testid="property-title" className={styles.title}>
          <span>{name}</span> <Stars stars={stars} size={14} color="grey2" />
        </h1>
      </header>

      <div className={styles.location}>
        <p className={styles.address}>
          <span>
            <Location size={16} />
            {address}
            {locationMessage()}
          </span>
        </p>

        <div className={styles.map}>
          <MapLink propertyId={propertyId} />
        </div>
      </div>
    </div>
  )
}

export default PropertyTitle
